import { useEffect, useState, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { api } from '../../lib/api'
import { Drawer } from '../ui/Drawer'
import { Button } from '../ui/Button'

const PlusIcon = <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>

interface SiteLicense {
  id: string
  site_id: string
  vms_type: string
  license_key: string | null
  total_count: number
  used_count: number
  vendor_account: string | null
  purchase_date: string | null
  expiry_date: string | null
  renewal_cost: number | null
  proposal_id: string | null
  proposal_slug: string | null
  proposal_title: string | null
  notes: string | null
  created_at: string
}

interface Props {
  siteId: string
  siteSlug: string
}

const VMS_NAMES: Record<string, string> = {
  dw_spectrum: 'DW Spectrum',
  milestone: 'Milestone XProtect',
  genetec: 'Genetec',
  avigilon: 'Avigilon',
}

function fmtMoney(n: number) {
  return '$' + n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })
}

function fmtDate(d: string | null | undefined): string {
  if (!d) return ''
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

function daysUntil(d: string | null | undefined): number | null {
  if (!d) return null
  const diff = new Date(d).getTime() - Date.now()
  return Math.ceil(diff / (1000 * 60 * 60 * 24))
}

export function SiteLicensesTab({ siteId: _siteId, siteSlug }: Props) {
  const [licenses, setLicenses] = useState<SiteLicense[]>([])
  const [loading, setLoading] = useState(true)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [editLicense, setEditLicense] = useState<SiteLicense | null>(null)
  const [detailLicense, setDetailLicense] = useState<SiteLicense | null>(null)
  const [saving, setSaving] = useState(false)
  const [formData, setFormData] = useState({
    vms_type: 'dw_spectrum',
    license_key: '',
    total_count: 0,
    used_count: 0,
    vendor_account: '',
    purchase_date: '',
    expiry_date: '',
    renewal_cost: 0,
    notes: '',
  })

  const loadLicenses = useCallback(async () => {
    try {
      const result = await api.get<SiteLicense[]>(`/sites/${siteSlug}/licenses`)
      setLicenses(result)
    } catch { /* handled */ }
    finally { setLoading(false) }
  }, [siteSlug])

  useEffect(() => { loadLicenses() }, [loadLicenses])

  function openCreate() {
    setEditLicense(null)
    setDetailLicense(null)
    setFormData({ vms_type: 'dw_spectrum', license_key: '', total_count: 0, used_count: 0, vendor_account: '', purchase_date: '', expiry_date: '', renewal_cost: 0, notes: '' })
    setDrawerOpen(true)
  }

  function openEdit(lic: SiteLicense) {
    setEditLicense(lic)
    setDetailLicense(null)
    setFormData({
      vms_type: lic.vms_type,
      license_key: lic.license_key || '',
      total_count: lic.total_count,
      used_count: lic.used_count,
      vendor_account: lic.vendor_account || '',
      purchase_date: lic.purchase_date || '',
      expiry_date: lic.expiry_date || '',
      renewal_cost: lic.renewal_cost || 0,
      notes: lic.notes || '',
    })
    setDrawerOpen(true)
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setSaving(true)
    try {
      const payload = {
        ...formData,
        license_key: formData.license_key || null,
        vendor_account: formData.vendor_account || null,
        purchase_date: formData.purchase_date || null,
        expiry_date: formData.expiry_date || null,
        renewal_cost: formData.renewal_cost || null,
        notes: formData.notes || null,
      }
      if (editLicense) {
        await api.patch(`/site-licenses/${editLicense.id}`, payload)
      } else {
        await api.post(`/sites/${siteSlug}/licenses`, payload)
      }
      setDrawerOpen(false)
      setEditLicense(null)
      loadLicenses()
    } catch { /* handled */ }
    finally { setSaving(false) }
  }

  async function deleteLicense(id: string) {
    if (!confirm('Delete this license record?')) return
    try {
      await api.delete(`/site-licenses/${id}`)
      setDetailLicense(null)
      loadLicenses()
    } catch { /* handled */ }
  }

  // Separate renewal alerts
  const renewalAlerts = licenses.filter(l => {
    const days = daysUntil(l.expiry_date)
    return days !== null && days > 0 && days <= 90
  })

  return (
    <div>
      <div className="flex justify-between items-center" style={{ marginBottom: 16 }}>
        <h3 style={{ fontSize: 18, fontWeight: 700 }}>Licenses</h3>
        <Button onClick={openCreate} icon={PlusIcon}>Add</Button>
      </div>

      {loading ? (
        <div className="skeleton" style={{ height: 200, borderRadius: 'var(--radius-lg)' }} />
      ) : (
        <>
          {/* Renewal Alerts */}
          {renewalAlerts.map(lic => {
            const days = daysUntil(lic.expiry_date)!
            return (
              <div
                key={`alert-${lic.id}`}
                className="p-4"
                style={{
                  background: 'var(--warning-muted)',
                  border: '1px solid var(--warning)',
                  borderRadius: 'var(--radius)',
                  marginBottom: 12,
                }}
              >
                <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--warning)' }}>
                  Renewal Alert
                </div>
                <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 4 }}>
                  {VMS_NAMES[lic.vms_type] || lic.vms_type}
                </div>
                <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 2 }}>
                  Expires: {fmtDate(lic.expiry_date)} ({days} days)
                </div>
                {lic.renewal_cost != null && lic.renewal_cost > 0 && (
                  <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 2 }}>
                    Renewal Cost: {fmtMoney(lic.renewal_cost)}/year
                  </div>
                )}
              </div>
            )
          })}

          {/* License Cards */}
          {licenses.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--text-tertiary)', fontSize: 14 }}>
              No licenses tracked yet. Add one to start tracking VMS licenses.
            </div>
          ) : (
            <div className="space-y-3">
              {licenses.map((lic) => {
                const usagePercent = lic.total_count > 0 ? Math.min(100, (lic.used_count / lic.total_count) * 100) : 0
                const available = lic.total_count - lic.used_count

                return (
                  <div
                    key={lic.id}
                    className="p-4"
                    style={{
                      background: 'var(--bg-surface)',
                      border: '1px solid var(--border)',
                      borderRadius: 'var(--radius)',
                      boxShadow: 'var(--shadow-xs)',
                    }}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)' }}>
                          {VMS_NAMES[lic.vms_type] || lic.vms_type}
                        </div>
                        {lic.license_key && (
                          <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 2, fontFamily: 'var(--font-mono, monospace)' }}>
                            Key: {lic.license_key}
                          </div>
                        )}

                        {/* Usage bar */}
                        <div style={{ marginTop: 10 }}>
                          <div style={{
                            height: 8, borderRadius: 4, background: 'var(--bg-elevated)',
                            overflow: 'hidden',
                          }}>
                            <div style={{
                              height: '100%', borderRadius: 4,
                              width: `${usagePercent}%`,
                              background: usagePercent > 90 ? 'var(--err)' : 'var(--primary)',
                              transition: 'width 0.3s',
                            }} />
                          </div>
                          <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>
                            {lic.used_count} / {lic.total_count} used
                          </div>
                        </div>

                        <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 6 }}>
                          {lic.purchase_date && <span>Purchased: {fmtDate(lic.purchase_date)}</span>}
                          {lic.proposal_slug && (
                            <span>{lic.purchase_date ? ' · ' : ''}
                              <Link to={`/proposals/${lic.proposal_slug}`} style={{ color: 'var(--primary)', textDecoration: 'none' }}>
                                {lic.proposal_title || `Proposal`}
                              </Link>
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 2 }}>
                          Expires: {lic.expiry_date ? fmtDate(lic.expiry_date) : 'Perpetual'}
                          {' · '}Available: {available} license{available !== 1 ? 's' : ''}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <button onClick={() => setDetailLicense(lic)} style={{ fontSize: 12, color: 'var(--primary)' }} className="hover:underline">
                          Details
                        </button>
                        <button onClick={() => openEdit(lic)} style={{ fontSize: 12, color: 'var(--primary)' }} className="hover:underline">
                          Edit
                        </button>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </>
      )}

      {/* Detail View */}
      <Drawer isOpen={!!detailLicense} onClose={() => setDetailLicense(null)} title={detailLicense ? (VMS_NAMES[detailLicense.vms_type] || detailLicense.vms_type) + ' Licenses' : ''}>
        {detailLicense && (() => {
          const lic = detailLicense
          const usagePercent = lic.total_count > 0 ? Math.min(100, (lic.used_count / lic.total_count) * 100) : 0
          const available = lic.total_count - lic.used_count

          return (
            <div className="space-y-4">
              <div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>License Key</div>
                <div style={{ fontSize: 14, fontFamily: 'var(--font-mono, monospace)', color: 'var(--text)' }}>
                  {lic.license_key || 'Not set'}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Vendor Account</div>
                <div style={{ fontSize: 14, color: 'var(--text)' }}>{lic.vendor_account || 'Not set'}</div>
              </div>
              <div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Purchase Date</div>
                <div style={{ fontSize: 14, color: 'var(--text)' }}>{fmtDate(lic.purchase_date) || 'Not set'}</div>
              </div>
              <div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Expiry</div>
                <div style={{ fontSize: 14, color: 'var(--text)' }}>{lic.expiry_date ? fmtDate(lic.expiry_date) : 'Perpetual'}</div>
              </div>
              {lic.proposal_slug && (
                <div>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Linked Proposal</div>
                  <Link to={`/proposals/${lic.proposal_slug}`} style={{ fontSize: 14, color: 'var(--primary)', textDecoration: 'none' }}>
                    {lic.proposal_title || 'View Proposal'}
                  </Link>
                </div>
              )}

              <div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 6 }}>Usage: {lic.used_count} / {lic.total_count}</div>
                <div style={{
                  height: 12, borderRadius: 6, background: 'var(--bg-elevated)',
                  overflow: 'hidden',
                }}>
                  <div style={{
                    height: '100%', borderRadius: 6,
                    width: `${usagePercent}%`,
                    background: usagePercent > 90 ? 'var(--err)' : 'var(--primary)',
                    transition: 'width 0.3s',
                  }} />
                </div>
                <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 6 }}>
                  Available: {available} license{available !== 1 ? 's' : ''}
                </div>
              </div>

              {lic.renewal_cost != null && lic.renewal_cost > 0 && (
                <div>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Renewal Cost</div>
                  <div style={{ fontSize: 14, color: 'var(--text)' }}>{fmtMoney(lic.renewal_cost)}/year</div>
                </div>
              )}

              {lic.notes && (
                <div>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Notes</div>
                  <div style={{ fontSize: 13, color: 'var(--text)' }}>{lic.notes}</div>
                </div>
              )}

              <div className="flex gap-2" style={{ paddingTop: 8 }}>
                <Button onClick={() => { setDetailLicense(null); openEdit(lic) }} size="sm">
                  Edit
                </Button>
                <Button onClick={() => deleteLicense(lic.id)} variant="danger" size="sm">
                  Delete
                </Button>
              </div>
            </div>
          )
        })()}
      </Drawer>

      {/* Add/Edit Form Drawer */}
      <Drawer isOpen={drawerOpen} onClose={() => { setDrawerOpen(false); setEditLicense(null) }} title={editLicense ? 'Edit License' : 'Add License'}>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="label" style={{ fontSize: 12, color: 'var(--text-secondary)' }}>VMS Type *</label>
            <select
              value={formData.vms_type}
              onChange={(e) => setFormData({ ...formData, vms_type: e.target.value })}
              className="input"
              style={{ fontSize: 14 }}
            >
              <option value="dw_spectrum">DW Spectrum</option>
              <option value="milestone">Milestone XProtect</option>
              <option value="genetec">Genetec</option>
              <option value="avigilon">Avigilon</option>
              <option value="custom">Custom</option>
            </select>
          </div>

          <div>
            <label className="label" style={{ fontSize: 12, color: 'var(--text-secondary)' }}>License Key</label>
            <input
              value={formData.license_key}
              onChange={(e) => setFormData({ ...formData, license_key: e.target.value })}
              className="input"
              placeholder="XXXX-XXXX-XXXX-XXXX"
              style={{ fontSize: 14, fontFamily: 'var(--font-mono, monospace)' }}
            />
          </div>

          <div className="flex gap-3">
            <div style={{ flex: 1 }}>
              <label className="label" style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Total Count *</label>
              <input
                type="number"
                min={0}
                value={formData.total_count}
                onChange={(e) => setFormData({ ...formData, total_count: parseInt(e.target.value) || 0 })}
                className="input"
                required
                style={{ fontSize: 14 }}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label className="label" style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Used Count</label>
              <input
                type="number"
                min={0}
                value={formData.used_count}
                onChange={(e) => setFormData({ ...formData, used_count: parseInt(e.target.value) || 0 })}
                className="input"
                style={{ fontSize: 14 }}
              />
            </div>
          </div>

          <div>
            <label className="label" style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Vendor Account</label>
            <input
              value={formData.vendor_account}
              onChange={(e) => setFormData({ ...formData, vendor_account: e.target.value })}
              className="input"
              placeholder="e.g. DW-12345"
              style={{ fontSize: 14 }}
            />
          </div>

          <div className="flex gap-3">
            <div style={{ flex: 1 }}>
              <label className="label" style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Purchase Date</label>
              <input
                type="date"
                value={formData.purchase_date}
                onChange={(e) => setFormData({ ...formData, purchase_date: e.target.value })}
                className="input"
                style={{ fontSize: 14 }}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label className="label" style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Expiry Date</label>
              <input
                type="date"
                value={formData.expiry_date}
                onChange={(e) => setFormData({ ...formData, expiry_date: e.target.value })}
                className="input"
                style={{ fontSize: 14 }}
              />
            </div>
          </div>

          <div>
            <label className="label" style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Renewal Cost ($/year)</label>
            <input
              type="number"
              min={0}
              step={0.01}
              value={formData.renewal_cost}
              onChange={(e) => setFormData({ ...formData, renewal_cost: parseFloat(e.target.value) || 0 })}
              className="input"
              style={{ fontSize: 14 }}
            />
          </div>

          <div>
            <label className="label" style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className="input"
              rows={3}
              style={{ fontSize: 14 }}
            />
          </div>

          <Button type="submit" loading={saving} className="w-full">
            {editLicense ? 'Save Changes' : 'Add License'}
          </Button>
        </form>
      </Drawer>
    </div>
  )
}
